import { LineChart } from "echarts/charts";
import { GridComponent, TitleComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { memo, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Spinner } from "@/components/ui/spinner";
import { useSupportedLocale } from "@/hooks/use-locale";
import { type TimeAxisPanelSpec } from "../ChartSpec";
import { useHoverTime } from "../crosshair/use-hover-time";
import { useCrosshairBus, useDataViewerStore, useWidthGroupRegistry } from "../data-viewer-provider";
import { pickResolution } from "../resolution/pick-resolution";
import type { RequestedStream } from "../stream-data/types";
import { useEnsureStreamData } from "../stream-data/use-ensure-stream-data";
import { useStreamData } from "../stream-data/use-stream-data";
import { useGetDisplayInfo } from "../units/use-get-display-info";
import { buildTimePanelOptions } from "./build-time-panel-options";
import { HoverValues } from "./hover-values/hover-values";
import { PlotChromeLayer } from "./layout/plot-chrome-layer";
import { usePanelPlotLayout } from "./layout/use-panel-plot-layout";
import { PanelLegend } from "./panel-legend";
import { PanelMetricBadge } from "./panel-metric-badge";
import { planPanelChrome } from "./plan-panel-chrome";
import { type GetConversion, prepareSeries } from "./prepare-series";
import { PANEL_WIDTH_GRIP_RESERVE_PX, ResizerGrip } from "./resize/resizer-grip";
import { usePanelResize } from "./resize/use-panel-resize";
import { UnitBadges } from "./unit-badge/unit-badges";
import { useLeftYAxisSelection } from "./use-left-y-axis-selection";

const DEFAULT_PANEL_HEIGHT_PX = 200;
const DEFAULT_PANEL_ROW_GAP_PX = 4;

echarts.use([LineChart, TitleComponent, TooltipComponent, GridComponent, CanvasRenderer]);

// Skip re-render when another panel's spec changes — this panel's props are unchanged.
export const Panel = memo(
    ({
        spec,
        groupId,
        maxStackWidth,
        syncCrosshair = true,
        emitsHoverTime = true,
        resizable = true,
        baseHeightPx = DEFAULT_PANEL_HEIGHT_PX,
    }: {
        spec: TimeAxisPanelSpec;
        /** Width group this panel shares gutters with — all panels in a stack pass the same id. */
        groupId: string;
        maxStackWidth: number;
        syncCrosshair?: boolean;
        emitsHoverTime?: boolean;
        resizable?: boolean;
        baseHeightPx?: number;
    }) => {
        const chartRef = useRef<HTMLDivElement | null>(null);
        const chartInstanceRef = useRef<echarts.ECharts | null>(null);
        const crosshairBus = useCrosshairBus();
        const hoverTime = useHoverTime();

        const startMs = useDataViewerStore((state) => state.startMs);
        const endMs = useDataViewerStore((state) => state.endMs);

        const gripReservePx = resizable ? PANEL_WIDTH_GRIP_RESERVE_PX + DEFAULT_PANEL_ROW_GAP_PX : 0;
        const maxWidth = Math.max(0, maxStackWidth - gripReservePx);
        const { size, heightHandleProps, widthHandleProps } = usePanelResize({
            maxWidth,
            defaultHeight: baseHeightPx,
        });
        const forceRaw = useDataViewerStore((state) => state.debugForceRawStreams);
        const resolution = forceRaw ? "raw" : pickResolution(size.width, startMs, endMs);

        const setPanelResolution = useDataViewerStore((state) => state.setPanelResolution);
        const clearPanelResolution = useDataViewerStore((state) => state.clearPanelResolution);
        useEffect(() => {
            setPanelResolution(groupId, spec.id, resolution);
            return () => clearPanelResolution(groupId, spec.id);
        }, [groupId, spec.id, resolution, setPanelResolution, clearPanelResolution]);

        const getDisplayInfo = useGetDisplayInfo();
        const locale = useSupportedLocale();
        const { leftAxes, activeLeftAxisId, setActiveLeftAxisId } = useLeftYAxisSelection(spec.id, spec.axes);

        // Strip display fields — fetch hooks only need streamId and how to aggregate its buckets.
        const requestedStreams = useMemo<RequestedStream[]>(
            () => spec.streams.map((stream) => ({ streamId: stream.streamId, aggregate: stream.aggregate })),
            [spec.streams],
        );

        // Kick off a fetch for any missing data; read windowed points from cache. See stream-data/README.md.
        const isFetching = useEnsureStreamData({ streams: requestedStreams, startMs, endMs, resolution });
        const data = useStreamData({ streams: requestedStreams, startMs, endMs, resolution });

        // Spinner only while fetching AND at least one stream has no data yet.
        // Panning into cached range shows data immediately; multi-stream waits for all.
        const isLoadingStreamData = isFetching && requestedStreams.some((stream) => !(stream.streamId in data));

        // Join cached points with spec (color, name, axis) and convert to display units.
        const preparedSeries = useMemo(() => {
            const getConversion: GetConversion = (unitCategory) => getDisplayInfo(unitCategory).fromDatabase;
            return prepareSeries(data, spec.streams, spec.axes, getConversion);
        }, [data, spec.streams, spec.axes, getDisplayInfo]);

        // Shared plot geometry: this panel's gutter requirement joins the stack's width group and
        // comes back as a grant that every panel in the group agrees on. Drives both the ECharts
        // grid and the DOM chrome layer, so they can't drift apart.
        const registry = useWidthGroupRegistry();
        const { layout, axisScales } = usePanelPlotLayout({
            axes: spec.axes,
            streams: spec.streams,
            preparedSeries,
            activeLeftAxisId,
            panelHeightPx: size.height,
            groupId,
            memberId: spec.id,
            registry,
        });

        const plotBottomPx = Math.max(size.height - layout.xAxisBandPx, 0);

        const chrome = useMemo(
            () => planPanelChrome({ axes: spec.axes, series: preparedSeries, plotBottomPx }),
            [spec.axes, preparedSeries, plotBottomPx],
        );

        const chartOptions = useMemo(
            () =>
                buildTimePanelOptions(
                    spec,
                    preparedSeries,
                    layout,
                    axisScales,
                    { startMs, endMs },
                    activeLeftAxisId,
                    getDisplayInfo,
                    locale,
                    chrome,
                ),
            [
                spec,
                preparedSeries,
                layout,
                axisScales,
                startMs,
                endMs,
                activeLeftAxisId,
                getDisplayInfo,
                locale,
                chrome,
            ],
        );

        // Ref so the init effect reads latest options without re-running.
        const chartOptionsRef = useRef(chartOptions);
        chartOptionsRef.current = chartOptions;

        // Init chart once on mount. Option updates go through setOption below.
        useLayoutEffect(() => {
            const chartContainer = chartRef.current;
            if (!chartContainer) return undefined;

            const existing = echarts.getInstanceByDom(chartContainer);
            if (existing) {
                crosshairBus.unregister(existing);
                existing.dispose();
            }

            const instance = echarts.init(chartContainer);
            instance.setOption(chartOptionsRef.current);

            if (syncCrosshair) crosshairBus.register(instance);
            if (emitsHoverTime) hoverTime.enable(instance);
            chartInstanceRef.current = instance;

            return () => {
                chartInstanceRef.current = null;
                hoverTime.disable();
                crosshairBus.unregister(instance);
                instance.dispose();
            };
        }, [syncCrosshair, emitsHoverTime, crosshairBus, hoverTime]);

        useEffect(() => {
            if (chartRef.current) {
                echarts.getInstanceByDom(chartRef.current)?.resize();
            }
        }, [size.height, size.width]);

        // Push option changes without tearing down the chart instance.
        // replaceMerge: shrinking series or y-axis lists must replace those arrays — default merge leaves orphans.
        //
        // useLayoutEffect so ECharts picks up gutter changes before the browser paints. useEffect would
        // flash the old (too narrow) grid for one frame when a sibling panel mounts and widens the stack.
        useLayoutEffect(() => {
            if (chartRef.current) {
                echarts.getInstanceByDom(chartRef.current)?.setOption(chartOptions, {
                    replaceMerge: ["series", "yAxis"],
                });
            }
        }, [chartOptions]);

        const noDataForRange =
            requestedStreams.length > 0 && preparedSeries.every((series) => series.points.length === 0);

        return (
            <div className={"mb-2 w-full"}>
                <div className={"inline-flex max-w-full"}>
                    {/* items-start: centering this column would shift the height grip away from the chart's left edge. */}
                    <div className={"flex flex-col items-start gap-0.5"}>
                        <div className={"flex shrink-0 items-start gap-1"}>
                            <div className={"flex shrink-0 flex-col"} style={{ width: size.width }}>
                                <div className={"relative shrink-0"} style={{ height: size.height }}>
                                    <div ref={chartRef} className={"size-full"} />

                                    {/* Chrome layer's box IS the plot rect: it carries the crisp 1px grid
                                        border and anchors badges to the plot corners. */}
                                    <PlotChromeLayer layout={layout}>
                                        <PanelMetricBadge
                                            unitCategory={chrome.metricCategory}
                                            deviceName={chrome.deviceName}
                                        />
                                        <UnitBadges
                                            axes={spec.axes}
                                            getDisplayInfo={getDisplayInfo}
                                            activeLeftAxisId={activeLeftAxisId}
                                            leftAxes={leftAxes}
                                            onActiveLeftAxisChange={setActiveLeftAxisId}
                                        />
                                    </PlotChromeLayer>

                                    <HoverValues
                                        chartRef={chartInstanceRef}
                                        series={preparedSeries}
                                        plotBottomPx={plotBottomPx}
                                        panelWidthPx={size.width}
                                        hoverTags={chrome.hoverTags}
                                    />

                                    {!isLoadingStreamData && noDataForRange && (
                                        <div
                                            className={
                                                "absolute inset-0 flex items-center justify-center bg-background/60"
                                            }
                                        >
                                            <div className={"text-sm text-muted-foreground"}>No data for range</div>
                                        </div>
                                    )}
                                    {isLoadingStreamData ? (
                                        <div
                                            className={
                                                "absolute inset-0 flex items-center justify-center bg-background/60"
                                            }
                                        >
                                            <Spinner className={"size-6 text-muted-foreground"} />
                                        </div>
                                    ) : null}
                                </div>
                                <PanelLegend series={preparedSeries} layout={layout} parts={chrome.legend} />
                            </div>
                            {/* Plot height minus the x-axis band. Size this box to center the grip — don't offset the grip (flex margin on a centered child only moves half the distance). */}
                            {resizable && (
                                <div
                                    className={"flex shrink-0 items-center justify-center"}
                                    style={{ height: Math.max(size.height - layout.xAxisBandPx, 0) }}
                                >
                                    <ResizerGrip orientation={"vertical"} handleProps={widthHandleProps} />
                                </div>
                            )}
                        </div>
                        {/* Pad to plot gutters so the grip centers under the plot, not the full panel width. */}
                        {resizable && (
                            <div
                                className={"flex justify-center"}
                                style={{
                                    width: size.width,
                                    paddingLeft: layout.gutters.left,
                                    paddingRight: layout.gutters.right,
                                }}
                            >
                                <ResizerGrip orientation={"horizontal"} handleProps={heightHandleProps} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    },
);
Panel.displayName = "Panel";
